import {
    Badge,
    Button,
    ProductCard,
    StoreCard as KamanStoreCard,
    Typography,
    Divider,
} from '@kaman/uikit';
import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useQuery} from 'react-query';
import {useDispatch} from 'react-redux';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {
    setSheetHeader,
    SHEET_HEADER_DEFAULTS,
} from '../../redux/sheetHeader/sheetHeaderSlice';
import {GetStoreInfo, GetStoreProducts} from '../../services/store.service';
import StoreDetailSkeleton from './components/storeDetailSkeleton';
import Banners from '../../components/banners/Banners';

const StoreDetail = () => {
    const [searchParams] = useSearchParams();
    const storeId = searchParams.get('storeId');
    const numericStoreId = Number(storeId);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const descriptionRef = useRef<HTMLElement>(null);
    const categoryDragRef = useRef<{startX: number; scrollLeft: number} | null>(
        null
    );
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [isDescriptionTruncated, setIsDescriptionTruncated] = useState(false);
    const isValidStoreId =
        Number.isInteger(numericStoreId) && numericStoreId > 0;

    const {
        data: storeInfo,
        isLoading,
        isError,
    } = useQuery(
        ['store-info', numericStoreId],
        () => GetStoreInfo({storeId: numericStoreId}),
        {enabled: isValidStoreId}
    );

    const {data: storeProducts} = useQuery(
        ['store-products', numericStoreId],
        () => GetStoreProducts({storeId: numericStoreId}),
        {enabled: isValidStoreId}
    );

    useEffect(() => {
        dispatch(
            setSheetHeader({
                title: '',
                showShare: false,
                showBack: true,
                headerBackground:
                    'linear-gradient(90deg, #1565D8 0%, #2196F3 50%, #00BCD4 100%)',
                headerBackgroundImage: '/images/circle-type2.svg',
                headerHeight: '10svh',
                contentOverlap: true,
                contentPadding: '0px',
            })
        );
    }, [dispatch, isLoading]);

    useEffect(
        () => () => {
            dispatch(
                setSheetHeader({
                    headerBackground: SHEET_HEADER_DEFAULTS.headerBackground,
                    headerBackgroundImage:
                        SHEET_HEADER_DEFAULTS.headerBackgroundImage,
                    contentOverlap: false,
                    headerHeight: 'auto',
                })
            );
        },
        [dispatch]
    );

    useEffect(() => {
        setIsDescriptionExpanded(false);
    }, [storeInfo?.description]);

    useLayoutEffect(() => {
        if (isDescriptionExpanded) return;

        const descriptionElement = descriptionRef.current;
        if (!descriptionElement) return;

        const updateTruncationState = () => {
            setIsDescriptionTruncated(
                descriptionElement.scrollHeight >
                    descriptionElement.clientHeight + 1
            );
        };

        updateTruncationState();
        const resizeObserver = new ResizeObserver(updateTruncationState);
        resizeObserver.observe(descriptionElement);

        return () => resizeObserver.disconnect();
    }, [isDescriptionExpanded, storeInfo?.description]);

    if (isLoading) {
        return <StoreDetailSkeleton />;
    }

    if (!isValidStoreId || isError || !storeInfo) return null;

    const storeMode = {
        OnlineAndOnsite: {
            supportsOnline: true,
            supportsOnsite: true,
        },
        Online: {
            supportsOnline: true,
            supportsOnsite: false,
        },
        Onsite: {
            supportsOnline: false,
            supportsOnsite: true,
        },
    }[storeInfo.type];

    const branchCount = storeInfo.branches?.length ?? 0;
    const hasMultipleBranches = branchCount > 1;
    const hasSingleBranch = branchCount === 1;
    const showBranchButton = storeMode.supportsOnsite && hasMultipleBranches;
    const storeCardMobileHeight =
        storeMode.supportsOnline && showBranchButton
            ? 'h-[42svh]'
            : storeMode.supportsOnline || showBranchButton
              ? 'h-[35svh]'
              : 'h-[32svh]';
    const showBranchBadge = showBranchButton;
    const singleBranch = hasSingleBranch ? storeInfo.branches[0] : undefined;
    const hasSingleBranchData = Boolean(
        singleBranch?.title ||
            singleBranch?.address ||
            singleBranch?.phoneNumbers?.length
    );
    const showAddressSection =
        storeMode.supportsOnsite && hasSingleBranch && hasSingleBranchData;
    const hasBranchCoordinates = Boolean(
        singleBranch &&
            Number.isFinite(singleBranch.lat) &&
            Number.isFinite(singleBranch.lng)
    );
    const showAboutSection = Boolean(storeInfo.description) || showBranchBadge;
    const showCategoriesSection = Boolean(storeInfo.categories?.length);
    const showProductsSection = Boolean(storeProducts?.length);

    const navigateToRoute = (route?: string) => {
        if (!route) return;

        if (/^https?:\/\//i.test(route)) {
            window.open(route, '_blank', 'noopener,noreferrer');
            return;
        }

        navigate(route);
    };

    return (
        <main>
            <div className="px-5 py-4">
                <KamanStoreCard
                    data-section="store-card"
                    backgroundColor="rgba(255, 255, 255, 0.6)"
                    className={`relative z-20 -mt-[6svh] ${storeCardMobileHeight} [@media(min-width:350px)]:h-[20svh] [&_img[alt$=banner]]:h-[60px]`}
                    style={{
                        backdropFilter: 'blur(5.5px)',
                        WebkitBackdropFilter: 'blur(5.5px)',
                        border: '1px solid rgba(7,33,70,0.2)',
                    }}
                    title={storeInfo.title}
                    online={storeMode.supportsOnline}
                    offline={storeMode.supportsOnsite}
                    hasBranches={showBranchButton}
                    banner={storeInfo.bannerUrl}
                    logo={storeInfo.logoUrl}
                    onOnlineClick={() => {
                        if (!storeInfo.url) return;

                        const url = storeInfo.url.startsWith('http')
                            ? storeInfo.url
                            : `https://${storeInfo.url}`;
                        window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                    onBranchesClick={() =>
                        navigate(
                            `/home/store-detail/branches?storeId=${storeId}`
                        )
                    }
                />
            </div>

            {showAboutSection && (
                <section data-section="about-store" className="px-5  py-5">
                    <div className="mb-3 flex items-center justify-between gap-3">
                        <Typography
                            as="h2"
                            variant="h6"
                            className="text-gray-900"
                        >
                            درباره فروشگاه
                        </Typography>
                        {showBranchBadge && (
                            <Badge
                                text={`${branchCount.toLocaleString('fa-IR')} شعبه`}
                                variant="outlined"
                                textColor="primary"
                                size="md"
                                className="border border-[#007AFF]"
                            />
                        )}
                    </div>

                    {storeInfo.description && (
                        <div className="relative">
                            <Typography
                                ref={descriptionRef}
                                as="p"
                                variant="body-small"
                                color="gray"
                                className={`text-justify text-gray-900 leading-7 ${
                                    isDescriptionExpanded ? '' : 'line-clamp-3'
                                }`}
                            >
                                {storeInfo.description}
                                {isDescriptionTruncated &&
                                    isDescriptionExpanded && (
                                        <>
                                            {' '}
                                            <button
                                                type="button"
                                                className="whitespace-nowrap text-sm font-bold text-[#0057D9]"
                                                onClick={() =>
                                                    setIsDescriptionExpanded(
                                                        false
                                                    )
                                                }
                                                aria-expanded={true}
                                            >
                                                مشاهده کمتر
                                            </button>
                                        </>
                                    )}
                            </Typography>

                            {isDescriptionTruncated &&
                                !isDescriptionExpanded && (
                                    <button
                                        type="button"
                                        className="absolute bottom-0 end-0 h-7 whitespace-nowrap bg-white ps-2 text-sm font-bold text-[#0057D9]"
                                        onClick={() =>
                                            setIsDescriptionExpanded(
                                                (isExpanded) => !isExpanded
                                            )
                                        }
                                        aria-expanded={isDescriptionExpanded}
                                    >
                                        ... مشاهده بیشتر
                                    </button>
                                )}
                        </div>
                    )}
                </section>
            )}
            <Divider className="h-1 bg-[#F6F6F6]" />

            {showAddressSection && singleBranch && (
                <section data-section="store-address" className="px-5 py-5">
                    <div className="mb-2 flex items-center justify-between gap-3">
                        <Typography
                            as="h2"
                            variant="h6"
                            className="text-gray-900"
                        >
                            آدرس فروشگاه
                        </Typography>
                        {hasBranchCoordinates && (
                            <Button
                                variant="ghost"
                                size="sm"
                                disablePadding
                                className="underline underline-offset-2 font-bold"
                                onClick={() => {
                                    const mapUrl = `https://www.google.com/maps/search/?api=1&query=${singleBranch.lat},${singleBranch.lng}`;
                                    window.open(
                                        mapUrl,
                                        '_blank',
                                        'noopener,noreferrer'
                                    );
                                }}
                                aria-label="مسیریابی به فروشگاه"
                            >
                                مسیریابی
                            </Button>
                        )}
                    </div>
                    {singleBranch.address && (
                        <Typography
                            as="address"
                            variant="body-small"
                            className="mt-2 not-italic leading-7 text-gray-900"
                        >
                            {singleBranch.address}
                        </Typography>
                    )}
                </section>
            )}
            {showAddressSection && singleBranch && (
                <Divider className="h-1 bg-[#F6F6F6]" />
            )}

            {showCategoriesSection && (
                <section data-section="store-categories" className="px-5 py-5">
                    <Typography
                        as="h2"
                        variant="h6"
                        className="mb-3 text-gray-900"
                    >
                        زمینه فعالیت فروشگاه
                    </Typography>
                    <div
                        dir="rtl"
                        className="flex w-full min-w-0 flex-nowrap gap-2 px-[2px] overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [@media(pointer:fine)]:cursor-grab [@media(pointer:fine)]:active:cursor-grabbing"
                        style={{
                            overscrollBehaviorX: 'contain',
                            WebkitOverflowScrolling: 'touch',
                            touchAction: 'auto',
                        }}
                        onMouseDown={(event) => {
                            if (event.button !== 0) return;
                            categoryDragRef.current = {
                                startX: event.clientX,
                                scrollLeft: event.currentTarget.scrollLeft,
                            };
                        }}
                        onMouseMove={(event) => {
                            const drag = categoryDragRef.current;
                            if (!drag) return;
                            const distance = event.clientX - drag.startX;
                            if (Math.abs(distance) < 6) return;
                            event.preventDefault();
                            event.currentTarget.scrollLeft =
                                drag.scrollLeft - distance * 1.2;
                        }}
                        onMouseUp={() => {
                            categoryDragRef.current = null;
                        }}
                        onMouseLeave={() => {
                            categoryDragRef.current = null;
                        }}
                        onDragStart={(event) => event.preventDefault()}
                    >
                        {storeInfo.categories.map((category) => (
                            <Badge
                                key={category.id}
                                text={category.title}
                                variant="outlined"
                                size="md"
                                className="shrink-0 select-none whitespace-nowrap"
                            />
                        ))}
                    </div>
                </section>
            )}

            <Banners bannerType="StoreInfo" />

            {showProductsSection && storeProducts && (
                <section data-section="store-products" className="px-5 py-5">
                    <Typography
                        as="h2"
                        variant="h6"
                        className="mb-3 text-gray-900"
                    >
                        محصولات
                    </Typography>
                    <div className="grid grid-cols-2 gap-3">
                        {storeProducts.map((product, index) => {
                            const productKey = `${product.route}-${product.title}-${index}`;
                            const hasDiscount =
                                product.price !== null &&
                                product.discountedPrice !== null &&
                                product.discountedPercent !== null &&
                                product.discountedPercent > 0;

                            const productCard = (
                                <ProductCard
                                    key={productKey}
                                    style={{width: '100%'}}
                                    className={
                                        product.route
                                            ? 'cursor-pointer'
                                            : undefined
                                    }
                                    title={product.title}
                                    subtitle={product.description1 ?? ''}
                                    storeTitle={product.description2 ?? ''}
                                    imageSrc={product.imageUrl}
                                    logoUrl={product.logoUrl ?? ''}
                                    badgeText={product.badge ?? ''}
                                    currency={product?.currency ?? ''}
                                    titleMaxLength={27}
                                    price={
                                        hasDiscount
                                            ? (product?.discountedPrice?.toLocaleString(
                                                  'fa'
                                              ) ?? '')
                                            : (product?.price?.toLocaleString(
                                                  'fa'
                                              ) ?? '')
                                    }
                                    originalPrice={
                                        hasDiscount
                                            ? (product?.price?.toLocaleString(
                                                  'fa'
                                              ) ?? undefined)
                                            : undefined
                                    }
                                    discountPercent={
                                        hasDiscount
                                            ? (product.discountedPercent ??
                                              undefined)
                                            : undefined
                                    }
                                    onClick={() =>
                                        navigate('/home/product-preview', {
                                            state: product,
                                        })
                                    }
                                />
                            );

                            return productCard;
                        })}
                    </div>
                </section>
            )}
        </main>
    );
};

export default StoreDetail;
